(() => {
  const attributeNames = [
    'customs_purchase_type',
    'customs_registered_company',
    'customs_eori_status',
    'customs_eori_number',
    'customs_vat_number',
    'customs_contact_differs',
    'customs_contact_name',
    'customs_contact_email',
    'customs_contact_phone',
    'customs_contact_acknowledged',
    'customs_clearance_acknowledged',
  ];

  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const cartUpdateUrl = () => `${cartRoot()}cart/update.js`;

  function radioValue(root, attributeName) {
    const checked = root.querySelector(`input[type="radio"][name="attributes[${attributeName}]"]:checked`);
    return checked ? checked.value : '';
  }

  function control(root, attributeName, type) {
    return root.querySelector(`input[type="${type}"][name="attributes[${attributeName}]"]`);
  }

  function clearGroup(group) {
    group.querySelectorAll('input').forEach((input) => {
      if (input.type === 'radio' || input.type === 'checkbox') input.checked = false;
      else input.value = '';
    });
  }

  function setVisibility(group, visible) {
    if (!group) return;
    group.hidden = !visible;
    if (!visible) clearGroup(group);
  }

  function syncConditionalFields(root) {
    const purchaseType = radioValue(root, 'customs_purchase_type');
    const enterprise = purchaseType === 'enterprise';
    const enterpriseGroup = root.querySelector('[data-customs-enterprise]');
    setVisibility(enterpriseGroup, enterprise);

    const eoriStatus = enterprise ? radioValue(root, 'customs_eori_status') : '';
    const eoriNumberGroup = root.querySelector('[data-customs-eori-number]');
    setVisibility(eoriNumberGroup, enterprise && eoriStatus === 'has_eori');

    const contactDiffers = enterprise && Boolean(control(root, 'customs_contact_differs', 'checkbox')?.checked);
    const contactGroup = root.querySelector('[data-customs-contact]');
    setVisibility(contactGroup, contactDiffers);
  }

  function collectValues(root) {
    const purchaseType = radioValue(root, 'customs_purchase_type');
    const enterprise = purchaseType === 'enterprise';
    const eoriStatus = enterprise ? radioValue(root, 'customs_eori_status') : '';
    const contactDiffers = enterprise && Boolean(control(root, 'customs_contact_differs', 'checkbox')?.checked);
    const valueOf = (name) => control(root, name, 'text')?.value.trim() || '';
    const rawValueOf = (name) => control(root, name, 'text')?.value ?? '';

    const values = {
      customs_purchase_type: purchaseType,
      customs_registered_company: enterprise ? valueOf('customs_registered_company') : '',
      customs_eori_status: eoriStatus,
      customs_eori_number: enterprise && eoriStatus === 'has_eori' ? rawValueOf('customs_eori_number') : '',
      customs_vat_number: enterprise ? rawValueOf('customs_vat_number') : '',
      customs_contact_differs: contactDiffers ? 'yes' : '',
      customs_contact_name: contactDiffers ? valueOf('customs_contact_name') : '',
      customs_contact_email: contactDiffers ? (control(root, 'customs_contact_email', 'email')?.value.trim() || '') : '',
      customs_contact_phone: contactDiffers ? (control(root, 'customs_contact_phone', 'tel')?.value.trim() || '') : '',
      customs_contact_acknowledged: contactDiffers && Boolean(control(root, 'customs_contact_acknowledged', 'checkbox')?.checked) ? 'yes' : '',
      customs_clearance_acknowledged: Boolean(control(root, 'customs_clearance_acknowledged', 'checkbox')?.checked) ? 'yes' : '',
    };

    return Object.fromEntries(attributeNames.map((name) => [name, String(values[name] ?? '')]));
  }

  function setStatus(root, message, state) {
    const status = root.querySelector('[data-customs-save-status]');
    if (!status) return;
    status.textContent = message;
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  }

  function stateFor(root) {
    if (!root.customsClearanceState) {
      root.customsClearanceState = {
        chain: Promise.resolve(),
        dirty: false,
        timer: null,
        pending: 0,
        revision: 0,
        replayingCheckout: false,
        emptyCleanupStarted: false,
      };
    }
    return root.customsClearanceState;
  }

  function queueSave(root) {
    const state = stateFor(root);
    const values = collectValues(root);
    const revision = ++state.revision;
    state.dirty = false;
    state.pending += 1;
    setStatus(root, root.dataset.messageSaving, 'saving');

    const save = async () => {
      const response = await fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: values }),
      });
      const cart = await response.json();
      if (!response.ok || !cart || !cart.attributes) {
        throw new Error('The cart did not confirm the customs attributes.');
      }
      const mismatched = attributeNames.some((name) => String(cart.attributes[name] ?? '') !== values[name]);
      if (mismatched) throw new Error('The cart response did not match the latest customs details.');
    };

    const next = state.chain.then(save, save);
    state.chain = next.catch(() => {});
    return next.then(
      () => {
        if (revision === state.revision) {
          state.lastError = null;
          setStatus(root, state.emptyCleanupStarted ? root.dataset.messageCartEmpty : root.dataset.messageSaved, 'saved');
        }
      },
      (error) => {
        state.lastError = error;
        if (revision === state.revision) {
          setStatus(root, root.dataset.messageSaveError, 'error');
        }
        throw error;
      }
    ).finally(() => {
      state.pending = Math.max(0, state.pending - 1);
    });
  }

  function scheduleSave(root, immediate) {
    const state = stateFor(root);
    state.dirty = true;
    setStatus(root, root.dataset.messageUnsaved, 'saving');
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      state.timer = null;
      queueSave(root).catch(() => {});
    }, immediate ? 0 : 450);
  }

  async function flushPendingSaves(root) {
    const state = stateFor(root);
    window.clearTimeout(state.timer);
    state.timer = null;
    if (state.dirty || state.lastError) {
      state.lastError = null;
      await queueSave(root).catch(() => {});
    }
    while (state.pending > 0 || state.dirty) {
      await state.chain;
      if (state.dirty) await queueSave(root).catch(() => {});
      else if (state.pending > 0) await new Promise((resolve) => window.setTimeout(resolve, 25));
    }
    if (state.lastError) throw state.lastError;
  }

  function cartHasItems() {
    const cartItems = document.querySelector('#main-cart-items');
    if (!cartItems) return true;
    return cartItems.querySelectorAll('.cart-item').length > 0;
  }

  function clearCustomsDetailsWhenCartIsEmpty(root) {
    const state = stateFor(root);
    if (cartHasItems()) {
      state.emptyCleanupStarted = false;
      return;
    }
    if (state.emptyCleanupStarted) return;

    const currentValues = collectValues(root);
    if (!Object.values(currentValues).some(Boolean)) return;

    state.emptyCleanupStarted = true;
    clearGroup(root);
    syncConditionalFields(root);
    scheduleSave(root, true);
  }

  let emptyCartCheckTimer = null;
  function scheduleEmptyCartCheck() {
    window.clearTimeout(emptyCartCheckTimer);
    emptyCartCheckTimer = window.setTimeout(() => {
      document.querySelectorAll('[data-customs-clearance]').forEach(clearCustomsDetailsWhenCartIsEmpty);
    }, 0);
  }

  function initialize(root) {
    if (root.dataset.customsClearanceInitialized === 'true') return;
    root.dataset.customsClearanceInitialized = 'true';
    syncConditionalFields(root);
    const state = stateFor(root);
    if (window.location.hash === `#${root.id}`) root.open = true;

    root.addEventListener('input', (event) => {
      if (!event.target.matches('input')) return;
      if (!cartHasItems()) {
        clearGroup(root);
        clearCustomsDetailsWhenCartIsEmpty(root);
        return;
      }
      scheduleSave(root, false);
    });
    root.addEventListener('change', (event) => {
      if (!event.target.matches('input')) return;
      if (!cartHasItems()) {
        clearGroup(root);
        clearCustomsDetailsWhenCartIsEmpty(root);
        return;
      }
      syncConditionalFields(root);
      scheduleSave(root, true);
    });

    const cartForm = document.getElementById('cart');
    if (cartForm && cartForm.dataset.customsClearanceSubmitBound !== 'true') {
      cartForm.dataset.customsClearanceSubmitBound = 'true';
      cartForm.addEventListener('submit', async (event) => {
        const activeRoot = document.querySelector('[data-customs-clearance]') || root;
        const activeState = stateFor(activeRoot);
        if (activeState.replayingCheckout) {
          activeState.replayingCheckout = false;
          return;
        }
        if ((event.submitter && event.submitter.name !== 'checkout') || (!activeState.dirty && activeState.pending === 0 && activeState.timer === null && !activeState.lastError && !window.BochuPOReference?.needsSave())) return;
        event.preventDefault();
        if (activeState.checkoutPending) return;
        activeState.checkoutPending = true;
        try {
          if (window.BochuPOReference?.needsSave()) await window.BochuPOReference.flush();
          await flushPendingSaves(activeRoot);
        } catch {
          activeState.replayingCheckout = false;
          if (window.BochuPOReference?.needsSave()) window.BochuPOReference.focusError();
          else activeRoot.querySelector('[data-customs-save-status]')?.focus();
          return;
        } finally {
          activeState.checkoutPending = false;
        }
        activeState.replayingCheckout = true;
        if (typeof cartForm.requestSubmit === 'function') cartForm.requestSubmit(event.submitter);
        else cartForm.submit();
      });
    window.setTimeout(() => clearCustomsDetailsWhenCartIsEmpty(root), 0);
    }
  }

  function scan(node) {
    if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_NODE) return;
    if (node.matches?.('[data-customs-clearance]')) initialize(node);
    node.querySelectorAll?.('[data-customs-clearance]').forEach(initialize);
  }

  scan(document);
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach(scan));
    scheduleEmptyCartCheck();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
